import { type ReactNode } from "react";
import { type MessageProps } from "../Messages/Message";
import classNames from "classnames";

import { Button, type ButtonProps } from "~/common/components/Button/Button";
import { Link, type LinkProps } from "~/common/components/Link";
import { HOMEPAGE } from "~/utils/url/staticUrls";
import { useTranslation } from "react-i18next";

import classes from "./InformationPage.module.less";
import { Alert } from "~/common/m3/Alert/Alert";

export const InformationPage = (props: InformationPageProps) => (
  <CenterContents>
    {props.title}
    {!!props.subtitle && props.subtitle}

    <Image src={props.imageSrc} />
    {props.btnSetup && (
      <Button onClick={props.btnSetup.onClick} href={props.btnSetup.href} id={props.btnSetup.id}>
        {props.btnSetup.label}
      </Button>
    )}
    {!!props.informationMsg && <InformationMessage>{props.informationMsg}</InformationMessage>}
  </CenterContents>
);

export interface InformationPageProps {
  title: ReactNode;
  subtitle?: ReactNode;
  btnSetup?: {
    onClick?: () => void;
    href?: string;
    label: ReactNode;
    id?: string;
  };
  imageSrc: string;
  informationMsg?: ReactNode;
}

const CenterContents = ({ className, leftAlign, ...rest }: JSX.IntrinsicElements["div"] & { leftAlign?: boolean }) => (
  <div className={classNames(classes.centerContent, className, leftAlign && classes.leftAlign)} {...rest} />
);
InformationPage.CenterContents = CenterContents;

const Image = ({ className, ...rest }: JSX.IntrinsicElements["img"]) => (
  <img className={classNames(classes.image, className)} {...rest} />
);
InformationPage.Image = Image;

const Title = ({ className, as = "h1", ...rest }: BaseHtmlAttributes & VariableHtmlTag) => {
  const Tag = as;
  return <Tag className={classNames(classes.headline, className)} {...rest} />;
};
InformationPage.Title = Title;

const Subtitle = ({ className, as = "p", ...rest }: BaseHtmlAttributes & VariableHtmlTag) => {
  const Tag = as;
  return <Tag className={classNames(classes.subheadline, className)} {...rest} />;
};
InformationPage.Subtitle = Subtitle;

const InformationMessage = ({ type = "info", className, ...rest }: Partial<MessageProps>) => (
  <Alert severity={type} className={classNames(classes.schaInfoPageInformationMessage, "mt-60", className)} {...rest} />
);
InformationPage.InformationMessage = InformationMessage;

const GoHomepageLink = ({ className, ...rest }: Partial<LinkProps>) => {
  const { t } = useTranslation();
  return (
    <Link className={classNames(classes.goBackLink, className)} href={HOMEPAGE} {...rest}>
      {t("emptyComparison.backHome")}
    </Link>
  );
};
InformationPage.GoHomepageLink = GoHomepageLink;

const CtaButton = ({ className, ...rest }: Partial<ButtonProps>) => (
  <Button className={classNames(classes.ctaButton, className)} {...rest} />
);
InformationPage.CtaButton = CtaButton;
